import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pause, Play, RotateCcw, Trash2, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import {
	ApiError,
	type DefinitionDetail,
	type Instance,
	type Sample,
	api,
	keys,
	unwrap,
	useDefinition,
	useDeleteInstance,
	useInstance,
	useInstanceAction,
	useInvalidateFleet,
	useMeta,
} from '@/api';
import { Bindings } from '@/components/Bindings';
import { SmallMultiples } from '@/components/Charts';
import { PresetNote, PresetPicker } from '@/components/Presets';
import { PreviewPanel } from '@/components/Preview';
import { type JsonSchema, SchemaForm } from '@/components/SchemaForm';
import { Badge, Button, Card, Field, Input, PageHeader, Select, Spinner, StatusDot, Tabs, Textarea } from '@/components/ui';
import { formatInterval, timeAgo } from '@/lib/format';
import { useSampleStream } from '@/lib/live';

type Tab = 'live' | 'params' | 'faults' | 'bindings' | 'json';

type UpdateBody = {
	name: string;
	slug: string;
	sampleIntervalMs: number;
	seed: string;
	params: Record<string, unknown>;
	faults: Record<string, unknown>;
	tags: string[];
	state: Instance['state'];
};

const toUpdate = (i: Instance): UpdateBody => ({
	name: i.name,
	slug: i.slug,
	sampleIntervalMs: i.sampleIntervalMs,
	seed: i.seed,
	params: i.params,
	faults: i.faults,
	tags: i.tags,
	state: i.state,
});

/** PUT with the revision we edited, so concurrent edits are detected instead of overwritten. */
function useSave(instance: Instance | undefined, onFieldErrors: (e: Record<string, string>) => void) {
	const qc = useQueryClient();
	const invalidate = useInvalidateFleet();
	return useMutation({
		mutationFn: (patch: Partial<UpdateBody>) =>
			unwrap(
				api.PUT('/api/v1/instances/{id}', {
					params: { path: { id: instance!.id }, header: { 'If-Match': String(instance!.revision) } },
					body: { ...toUpdate(instance!), ...patch },
				}),
			),
		onSuccess: (i) => {
			qc.setQueryData(keys.instance(i.id), i);
			invalidate();
			onFieldErrors({});
			toast.success('Saved — applied to the running sensor');
		},
		onError: (e) => {
			if (e instanceof ApiError && e.status === 412) toast.error('Someone else changed this sensor. Reload to see their version.');
			else toast.error(e.message);
			if (e instanceof ApiError) onFieldErrors(e.fieldErrors());
		},
	});
}

export function InstanceDetail() {
	const { id = '' } = useParams();
	const inst = useInstance(id);
	const def = useDefinition(inst.data?.definitionId);
	const [tab, setTab] = useState<Tab>('live');
	const action = useInstanceAction();
	const del = useDeleteInstance();
	const navigate = useNavigate();
	const invalidate = useInvalidateFleet();

	if (inst.isError) return <p className="text-fg-muted">This sensor does not exist (any more).</p>;
	if (!inst.data || !def.data) return <Spinner />;
	const i = inst.data;
	const running = i.state === 'running';

	const run = async (what: 'reset' | 'clone') => {
		try {
			if (what === 'reset') {
				await unwrap(api.POST('/api/v1/instances/{id}/reset', { params: { path: { id } } }));
				toast.success('Generator state reset (counters, random walks)');
			} else {
				const c = await unwrap(api.POST('/api/v1/instances/{id}/clone', { params: { path: { id } }, body: {} }));
				invalidate();
				toast.success(`Cloned as ${c.name}`);
				navigate(`/instances/${c.id}`);
			}
		} catch (e) {
			toast.error((e as Error).message);
		}
	};

	return (
		<>
			<PageHeader
				title={
					<span className="flex items-center gap-3">
						{i.name} <StatusDot status={i.status} />
						{i.status === 'running' && !i.online && <Badge tone="warn">offline</Badge>}
					</span>
				}
				subtitle={
					<>
						<code className="font-mono">{i.slug}</code> ·{' '}
						<Link to={`/definitions/${i.definitionId}`} className="hover:underline">
							{def.data.name}
						</Link>{' '}
						· every {formatInterval(i.sampleIntervalMs)} · seed <code className="font-mono">{i.seed}</code>
						{i.tags.map((t) => (
							<Badge key={t} className="ml-1.5">
								{t}
							</Badge>
						))}
					</>
				}
				actions={
					<>
						<Button
							variant={running ? 'secondary' : 'primary'}
							onClick={() => action.mutate({ id, action: running ? 'stop' : 'start' }, { onSuccess: () => inst.refetch(), onError: (e) => toast.error(e.message) })}
						>
							{running ? <Pause className="size-4" /> : <Play className="size-4" />} {running ? 'Stop' : 'Start'}
						</Button>
						<Button onClick={() => run('reset')} disabled={!running} title="Restart counters and random walks">
							<RotateCcw className="size-4" /> Reset
						</Button>
						<Button onClick={() => run('clone')}>
							<Copy className="size-4" /> Clone
						</Button>
						<Button
							variant="danger"
							onClick={() =>
								confirm(`Delete ${i.name}? Its bindings stop answering immediately.`) &&
								del.mutate(id, { onSuccess: () => (toast.success('Deleted'), navigate('/instances')) })
							}
						>
							<Trash2 className="size-4" />
						</Button>
					</>
				}
			/>
			{i.statusDetail && <p className="mb-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">{i.statusDetail}</p>}
			<Tabs<Tab>
				value={tab}
				onChange={setTab}
				tabs={[
					{ id: 'live', label: 'Live' },
					{ id: 'params', label: 'Parameters' },
					{ id: 'faults', label: 'Faults' },
					{ id: 'bindings', label: `Bindings (${i.bindings.length})` },
					{ id: 'json', label: 'JSON' },
				]}
			/>
			<div className="pt-5">
				{tab === 'live' && <LiveTab instance={i} />}
				{tab === 'params' && <ParamsTab instance={i} definition={def.data} />}
				{tab === 'faults' && <FaultsTab instance={i} />}
				{tab === 'bindings' && <Bindings instance={i} supported={def.data.supportedProtocols} ambientMetrics={def.data.ambientMetrics} />}
				{tab === 'json' && <JsonTab instance={i} />}
			</div>
		</>
	);
}

function LiveTab({ instance }: { instance: Instance }) {
	const def = useDefinition(instance.definitionId);
	const history = useQuery({
		queryKey: ['samples', instance.id],
		queryFn: () => unwrap(api.GET('/api/v1/instances/{id}/samples', { params: { path: { id: instance.id }, query: { limit: 300 } } })).then((r) => r.items),
		staleTime: Infinity,
	});
	const buffer = useRef<Sample[]>([]);
	const [samples, setSamples] = useState<Sample[]>([]);
	useEffect(() => {
		if (history.data) {
			buffer.current = [...history.data];
			setSamples(buffer.current);
		}
	}, [history.data]);
	useSampleStream(instance.state === 'running' ? [instance.id] : [], ({ sample }) => {
		const b = buffer.current;
		if (b.length && b[b.length - 1]!.seq >= sample.seq && b[b.length - 1]!.ts >= sample.ts) return;
		b.push(sample);
		if (b.length > 600) b.shift();
	});
	useEffect(() => {
		const h = setInterval(() => setSamples([...buffer.current]), 500);
		return () => clearInterval(h);
	}, []);
	const last = samples[samples.length - 1];
	if (instance.state !== 'running') return <p className="text-sm text-fg-muted">The sensor is stopped. Start it to see live values.</p>;
	if (!def.data || history.isPending) return <Spinner />;
	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-fg-muted">
				<span>{last ? `Last sample ${timeAgo(last.ts)} · #${last.seq}` : 'Waiting for the first sample…'}</span>
				{last && last.quality !== 'good' && <Badge tone="danger">quality {last.quality}</Badge>}
				<span>
					{instance.emitted.toLocaleString()} emitted · {instance.dropped.toLocaleString()} dropped
				</span>
			</div>
			<SmallMultiples fields={def.data.fields} samples={samples} />
		</div>
	);
}

function ParamsTab({ instance, definition }: { instance: Instance; definition: DefinitionDetail }) {
	const meta = useMeta();
	const [params, setParams] = useState<unknown>(instance.params);
	const [general, setGeneral] = useState({ name: instance.name, slug: instance.slug, sampleIntervalMs: instance.sampleIntervalMs, seed: instance.seed, tags: instance.tags.join(', ') });
	const [errors, setErrors] = useState<Record<string, string>>({});
	const save = useSave(instance, setErrors);
	const dirty = JSON.stringify(params) !== JSON.stringify(instance.params) || JSON.stringify(general) !== JSON.stringify({ name: instance.name, slug: instance.slug, sampleIntervalMs: instance.sampleIntervalMs, seed: instance.seed, tags: instance.tags.join(', ') });
	const paramErrors = Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('/params')).map(([k, v]) => [k.slice(7), v]));
	return (
		<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
			<div className="flex min-w-0 flex-col gap-4">
				<Card title="General">
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
						<Field label="Name" error={errors['/name']}>
							<Input value={general.name} onChange={(e) => setGeneral({ ...general, name: e.target.value })} />
						</Field>
						<Field label="Slug" error={errors['/slug']} hint="Changing it does not rename existing topics.">
							<Input value={general.slug} onChange={(e) => setGeneral({ ...general, slug: e.target.value })} />
						</Field>
						<Field label="Sample every (ms)" error={errors['/sampleIntervalMs']} hint={`Minimum ${meta.data?.limits.minIntervalMs ?? 100} ms`}>
							<Input type="number" value={general.sampleIntervalMs} onChange={(e) => setGeneral({ ...general, sampleIntervalMs: Number(e.target.value) })} />
						</Field>
						<Field label="Seed" error={errors['/seed']} hint="Changing it restarts the generator.">
							<Input value={general.seed} onChange={(e) => setGeneral({ ...general, seed: e.target.value.replace(/\D/g, '') })} />
						</Field>
						<Field label="Tags" hint="Comma separated">
							<Input value={general.tags} onChange={(e) => setGeneral({ ...general, tags: e.target.value })} />
						</Field>
					</div>
				</Card>
				<Card title="Parameters" actions={<PresetPicker defaults={definition.defaults} presets={definition.presets} onPick={setParams} />}>
					<PresetNote params={params} presets={definition.presets} />
					<SchemaForm schema={definition.paramsSchema as JsonSchema} value={params} onChange={setParams} errors={paramErrors} />
				</Card>
				<div className="sticky bottom-3 flex justify-end gap-2 rounded-lg border border-border bg-surface/90 p-2 backdrop-blur">
					<span className="mr-auto self-center px-2 text-xs text-fg-muted">{dirty ? 'Unsaved changes' : `Revision ${instance.revision}`}</span>
					<Button
						disabled={!dirty}
						onClick={() => {
							setParams(instance.params);
							setGeneral({ name: instance.name, slug: instance.slug, sampleIntervalMs: instance.sampleIntervalMs, seed: instance.seed, tags: instance.tags.join(', ') });
							setErrors({});
						}}
					>
						Revert
					</Button>
					<Button
						variant="primary"
						disabled={!dirty || save.isPending}
						onClick={() =>
							save.mutate({
								...general,
								tags: general.tags.split(',').map((t) => t.trim()).filter(Boolean),
								params: params as Record<string, unknown>,
							})
						}
					>
						Save & apply
					</Button>
				</div>
			</div>
			<div className="min-w-0 xl:sticky xl:top-6 xl:self-start">
				<Card>
					<PreviewPanel definitionId={instance.definitionId} params={params} faults={instance.faults} seed={general.seed} />
				</Card>
			</div>
		</div>
	);
}

function FaultsTab({ instance }: { instance: Instance }) {
	const meta = useMeta();
	const [faults, setFaults] = useState<unknown>(instance.faults);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const save = useSave(instance, setErrors);
	const [kind, setKind] = useState<'dropout' | 'stuck' | 'bad_quality' | 'out_of_range' | 'offline'>('dropout');
	const [secs, setSecs] = useState(10);
	const trigger = useMutation({
		mutationFn: () => unwrap(api.POST('/api/v1/instances/{id}/faults/trigger', { params: { path: { id: instance.id } }, body: { kind, durationS: secs } })),
		onSuccess: () => toast.success(`Injected ${kind.replace('_', ' ')} for ${secs}s`),
		onError: (e) => toast.error(e.message),
	});
	const faultErrors = Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('/faults')).map(([k, v]) => [k.slice(7), v]));
	return (
		<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
			<Card
				title="Random faults"
				actions={
					<Button variant="primary" size="sm" disabled={save.isPending || JSON.stringify(faults) === JSON.stringify(instance.faults)} onClick={() => save.mutate({ faults: faults as Record<string, unknown> })}>
						Save & apply
					</Button>
				}
			>
				<p className="mb-3 text-xs text-fg-muted">Saved with the sensor and applied continuously.</p>
				{meta.data && <SchemaForm schema={meta.data.faultsSchema as JsonSchema} value={faults} onChange={setFaults} errors={faultErrors} />}
			</Card>
			<Card title="Inject now">
				<p className="mb-3 text-xs text-fg-muted">A one-off fault for a few seconds; not saved. Handy for testing alarms and timeouts.</p>
				<div className="flex flex-col gap-3">
					<Field label="Fault">
						<Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
							<option value="dropout">Dropout — samples lost</option>
							<option value="stuck">Stuck — values freeze</option>
							<option value="bad_quality">Bad quality — flagged bad</option>
							<option value="out_of_range">Out of range — impossible values</option>
							<option value="offline">Offline — device unreachable</option>
						</Select>
					</Field>
					<Field label="Duration (s)">
						<Input type="number" min={1} max={3600} value={secs} onChange={(e) => setSecs(Number(e.target.value))} />
					</Field>
					<Button variant="primary" disabled={instance.state !== 'running' || trigger.isPending} onClick={() => trigger.mutate()}>
						<Zap className="size-4" /> Inject
					</Button>
				</div>
			</Card>
		</div>
	);
}

function JsonTab({ instance }: { instance: Instance }) {
	const [text, setText] = useState(JSON.stringify(toUpdate(instance), null, 2));
	const [errors, setErrors] = useState<Record<string, string>>({});
	const save = useSave(instance, setErrors);
	let parsed: UpdateBody | undefined;
	try {
		parsed = JSON.parse(text);
	} catch {
		parsed = undefined;
	}
	return (
		<Card
			title="Raw configuration"
			actions={
				<Button variant="primary" size="sm" disabled={!parsed || save.isPending} onClick={() => parsed && save.mutate(parsed)}>
					Save & apply
				</Button>
			}
		>
			<Textarea rows={24} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
			{!parsed && <p className="mt-2 text-xs text-danger">Not valid JSON</p>}
			{Object.entries(errors).map(([k, v]) => (
				<p key={k} className="mt-1 text-xs text-danger">
					<code className="font-mono">{k}</code>: {v}
				</p>
			))}
		</Card>
	);
}
